/**
 * Per-user, per-company "view" preferences for the DMA PLG "Update products"
 * surface (PLT-4748).
 *
 * Replaces three ephemeral `useState` flags (`showMatching`, `showAttributes`,
 * and the new `viewMode`) with a single localStorage-backed JSON blob so
 * users keep their column / view choices across reloads and navigation.
 * Stored as one object per `(userId, companyId)` pair under a single key, so
 * adding future flags is a schema change to this object rather than a new
 * localStorage key.
 *
 * The lowercase-dotted key scheme (`piu.viewPrefs:<userId>:<companyId>`)
 * matches the newer `chat.*` keys in `src/ui/common/generic/utilities/
 * storage.ts` — the codebase is gradually migrating away from the legacy
 * `UPRIGHT/...` ALL-CAPS scheme. There's no legacy key to migrate from:
 * these flags were ephemeral `useState` before this PR.
 *
 * Subsequent PRs in the Rev 7 redesign (PLT-4747) will add more flags
 * (sort scope, hint dismissals, etc.) to this same blob.
 */
import { useCallback, useEffect, useState } from 'react';

export type SegmentViewMode = 'list' | 'hierarchy';

export interface SegmentViewPreferences {
  /** Show the "Matching" column with the matched product name. Team users only. */
  showMatching: boolean;
  /** Show the "Attributes" column with IPR attributes. Enterprise + team users. */
  showAttributes: boolean;
  /** List view (today's behavior) vs. read-only Hierarchy view (PLT-4748). */
  viewMode: SegmentViewMode;
}

export const DEFAULT_SEGMENT_VIEW_PREFERENCES: SegmentViewPreferences = {
  showMatching: false,
  showAttributes: false,
  // Hierarchy is the default view; the flat list is opt-in via the
  // "Show flat list" checkbox (PLT-5707).
  viewMode: 'hierarchy',
};

const STORAGE_KEY_PREFIX = 'piu.viewPrefs';

function getStorageKey(userId: number | undefined, companyId: string | undefined): string | undefined {
  // If either identifier is missing we fall through to in-memory defaults.
  // Guarding here keeps us from accidentally cross-contaminating a different
  // user / company's preferences via a falsy key.
  if (userId === undefined || !companyId) return undefined;
  return `${STORAGE_KEY_PREFIX}:${userId}:${companyId}`;
}

function loadFromStorage(key: string | undefined): SegmentViewPreferences {
  if (!key) return DEFAULT_SEGMENT_VIEW_PREFERENCES;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof localStorage === 'undefined') return DEFAULT_SEGMENT_VIEW_PREFERENCES;
  const raw = localStorage.getItem(key);
  if (raw === null) return DEFAULT_SEGMENT_VIEW_PREFERENCES;
  try {
    const parsed = JSON.parse(raw) as Partial<SegmentViewPreferences>;
    // Only accept known values. Anything malformed in storage (older shape,
    // hand-edited, etc.) falls back to the default rather than producing a
    // weird UI state.
    return {
      showMatching: parsed.showMatching === true,
      showAttributes: parsed.showAttributes === true,
      viewMode: parsed.viewMode === 'hierarchy' ? 'hierarchy' : 'list',
    };
  } catch {
    return DEFAULT_SEGMENT_VIEW_PREFERENCES;
  }
}

function saveToStorage(key: string | undefined, prefs: SegmentViewPreferences) {
  if (!key) return;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(key, JSON.stringify(prefs));
}

/**
 * Returns `[prefs, updatePrefs]` where `updatePrefs` accepts a partial patch
 * (so callers can flip one flag at a time without losing the others).
 * Reloads from storage when `userId` / `companyId` change so navigation
 * between companies doesn't leak preferences from the previous company.
 */
export function useSegmentViewPreferences(
  userId: number | undefined,
  companyId: string | undefined,
): readonly [SegmentViewPreferences, (patch: Partial<SegmentViewPreferences>) => void] {
  const storageKey = getStorageKey(userId, companyId);
  const [prefs, setPrefs] = useState<SegmentViewPreferences>(() => loadFromStorage(storageKey));

  useEffect(() => {
    setPrefs(loadFromStorage(storageKey));
  }, [storageKey]);

  const update = useCallback(
    (patch: Partial<SegmentViewPreferences>) => {
      setPrefs(prev => {
        const next = { ...prev, ...patch };
        saveToStorage(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  return [prefs, update] as const;
}
